import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { apiAds } from "@/common/services";
import { IStreamateCamera } from "@/common/api/services/types";
import { useDeviceType } from "@/common/hooks";
import { deviceTypeEnum } from "@/common/utils/device";
import { useStreamateCamerasStore } from "@/common/store/streamateCamerasStore";
import { metricsBus } from "@/common/buses";

const DESKTOP_PARAM = "1-2";
const MOBILE_PARAM = "1-5";

interface IProps {
  position: number;
}

export const useStreamateCamera = ({ position }: IProps) => {
  const [isSound, setSound] = useState(false);
  const [camera, setCamera] = useState<IStreamateCamera | null>(null);
  const [isError, setError] = useState<boolean>(false);

  const seenCameras = useStreamateCamerasStore((state) => state.seenCameras);
  const addSeenCamera = useStreamateCamerasStore((state) => state.addSeenCamera);

  const isDesktop = useDeviceType() === deviceTypeEnum.DESKTOP;

  const videoRef = useRef<HTMLVideoElement>(null);
  const viewedRef = useRef(false);

  const handleClick = useCallback(() => {
    if (camera === null) {
      console.debug("Camera not loaded, cannot handle click.");
      return;
    }

    if (isSound) {
      setSound(false);
    }

    const url = new URL(camera.url);
    url.searchParams.set("AFNO", isDesktop ? DESKTOP_PARAM : MOBILE_PARAM);

    console.debug(`Opening camera ${camera.nickname} in new tab, url=${url.toString()}`);

    metricsBus.emit("liveCamClicked", {
      creator: camera.nickname,
      video: camera.kwikyId,
      position,
    });

    window.open(url, "_blank");
  }, [camera, isSound, isDesktop, position]);

  const fetchCamera = useMemo(() => async () => {
    try {
      console.debug("Fetching Streamate camera...");

      const response = await apiAds.getStreamateCamera({
        exclude: seenCameras,
      });

      addSeenCamera(response.id);
      setCamera(response);
    } catch (e) {
      console.error("Error fetching camera:", e);
      setError(true);
    }
  }, []);

  const handleToggleSound = useCallback(() => {
    setSound(prev => !prev);
  }, []);

  const playHandler = useCallback(() => {
    if (camera === null) {
      return;
    }

    if (viewedRef.current) {
      return;
    }

    metricsBus.emit("liveCamView", {
      creator: camera.nickname,
      video: camera.kwikyId,
      position,
    });

    viewedRef.current = true;
  }, [camera]);

  useEffect(() => {
    fetchCamera();
  }, []);

  return { camera, handleClick, isError, handleToggleSound, isSound, videoRef, playHandler };
};
